import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Person } from './api';
import { AUTH_CHANGE_EVENT, getTokens } from './auth';
import { buildTabMembership } from './tabMembership';
import { useBootstrap } from './useBootstrap';
import { useMe } from './useMe';
import { useWorkspace, type WorkspaceId } from './WorkspaceContext';

export const RATER_KEY = 'starmap.activeRaterPid';

interface ActiveRaterState {
  activeRater: Person | null;
  activeRaterPid: string | null;
  caylentPeople: Person[];
  isLoading: boolean;
  isManager: boolean;
  setActiveRaterPid: (pid: string | null) => void;
}

const Ctx = createContext<ActiveRaterState | null>(null);

function storageKey(workspaceId: WorkspaceId): string {
  return `${RATER_KEY}.${workspaceId}`;
}

function readStoredRater(workspaceId: WorkspaceId): string | null {
  try {
    const scoped = window.localStorage.getItem(storageKey(workspaceId));
    if (scoped) return scoped;
    return workspaceId === 'aws' ? window.localStorage.getItem(RATER_KEY) : null;
  } catch {
    return null;
  }
}

function writeStoredRater(workspaceId: WorkspaceId, pid: string | null) {
  try {
    if (pid) window.localStorage.setItem(storageKey(workspaceId), pid);
    else window.localStorage.removeItem(storageKey(workspaceId));
  } catch {
    // Private browsing / locked-down storage should not block rating.
  }
}

export function ActiveRaterProvider({ children, prioritizeReview = false }: { children: ReactNode; prioritizeReview?: boolean }) {
  const { workspaceId } = useWorkspace();
  const me = useMe(workspaceId);
  // Review pages start this shared read after their own request and screen load.
  const boot = useBootstrap(!prioritizeReview);
  const [activeRaterPid, setActiveRaterPidState] = useState<string | null>(() =>
    readStoredRater(workspaceId),
  );

  useEffect(() => {
    setActiveRaterPidState(readStoredRater(workspaceId));
  }, [workspaceId]);

  const index = useMemo(() => (boot.data ? buildTabMembership(boot.data.people) : null), [boot.data]);

  const caylentPeople = useMemo<Person[]>(() => {
    if (!boot.data || !index) return [];
    return boot.data.people
      .filter((p) => index.tabOf.get(p.pid) === 'caylent')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [boot.data, index]);

  const caylentPidSet = useMemo(
    () => new Set(caylentPeople.map((p) => p.pid)),
    [caylentPeople],
  );

  useEffect(() => {
    if (!boot.data || me.isLoading) return;

    setActiveRaterPidState((current) => {
      if (current && caylentPidSet.has(current)) return current;

      const linkedPid = me.data?.person?.pid ?? null;
      if (linkedPid && caylentPidSet.has(linkedPid)) return linkedPid;

      return null;
    });
  }, [boot.data, caylentPidSet, me.data?.person?.pid, me.isLoading]);

  useEffect(() => {
    writeStoredRater(workspaceId, activeRaterPid);
  }, [activeRaterPid, workspaceId]);

  useEffect(() => {
    const syncStoredRater = () => {
      if (!getTokens()) {
        setActiveRaterPidState(null);
        return;
      }
      setActiveRaterPidState(readStoredRater(workspaceId));
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === RATER_KEY || event.key?.startsWith('starmap.')) syncStoredRater();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_CHANGE_EVENT, syncStoredRater);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_CHANGE_EVENT, syncStoredRater);
    };
  }, [workspaceId]);

  const setActiveRaterPid = useCallback((pid: string | null) => {
    setActiveRaterPidState(pid);
  }, []);

  const activeRater = useMemo(
    () => caylentPeople.find((p) => p.pid === activeRaterPid) ?? null,
    [activeRaterPid, caylentPeople],
  );

  const isManager = useMemo(() => {
    if (!boot.data || !activeRaterPid) return false;
    if (boot.data.people.some((p) => p.manager_pid === activeRaterPid)) return true;
    return Boolean(boot.data.reference_data.cy_manager?.[activeRaterPid]);
  }, [activeRaterPid, boot.data]);

  const value = useMemo<ActiveRaterState>(
    () => ({
      activeRater,
      activeRaterPid,
      caylentPeople,
      isLoading: boot.isLoading || (prioritizeReview && !boot.data) || me.isLoading,
      isManager,
      setActiveRaterPid,
    }),
    [
      activeRater,
      activeRaterPid,
      boot.isLoading,
      boot.data,
      prioritizeReview,
      caylentPeople,
      isManager,
      me.isLoading,
      setActiveRaterPid,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveRater(): ActiveRaterState {
  const value = useContext(Ctx);
  if (!value) throw new Error('useActiveRater must be used inside <ActiveRaterProvider>');
  return value;
}
